import {
  initKokoro,
  speakKokoroText,
  queueKokoroText,
  stopKokoroSpeech,
} from "./libs/kokoroTTS";

export class TTSManager {
  // ── Provider registry ──────────────────────────────────────────
  // Add new TTS providers here. Each key must match the prefix used
  // in provider strings (e.g. 'kokoro' matches 'kokoro_af_nicole').

  static adapters = {
    kokoro: {
      init: initKokoro,
      queueText: queueKokoroText,
      speakText: speakKokoroText,
      stopSpeech: stopKokoroSpeech,
      extractVoice: (provider) => {
        if (provider && provider.startsWith("kokoro_")) {
          return provider.slice("kokoro_".length);
        }
        return "af_nicole"; // fallback
      },
    },
    // Example — add more providers:
    // elevenlabs: { init: initEleven, speakText: speakEleven, … },
    // browser:   { speakText: speakWithBrowserTTS, … },
  };

  // ── Enabled providers (Set of keys from the registry above) ────

  static enabledProviders = new Set([]); // currently all disabled

  // ── Convenience toggles ────────────────────────────────────────

  static enableProvider(key) {
    if (this.adapters[key]) {
      this.enabledProviders.add(key);
    } else {
      console.warn(`[TTSManager] Unknown provider "${key}"`);
    }
  }

  static disableProvider(key) {
    this.enabledProviders.delete(key);
  }

  static isProviderEnabled(key) {
    return this.enabledProviders.has(key);
  }

  // ── Internal helpers ───────────────────────────────────────────

  static getActiveAdapters() {
    const active = {};
    for (const [key, adapter] of Object.entries(this.adapters)) {
      if (this.enabledProviders.has(key)) {
        active[key] = adapter;
      }
    }
    return active;
  }

  // ── Provider management ────────────────────────────────────────

  static provider = null; // Default provider – set via setProvider()

  static setProvider(providerName) {
    this.provider = providerName;
  }

  static isProvider(type, provider = this.provider) {
    return !!(
      provider &&
      typeof provider === "string" &&
      provider.startsWith(type)
    );
  }

  static getAdapter(providerName = this.provider) {
    if (!providerName || typeof providerName !== "string") return null;
    const adapters = this.getActiveAdapters();
    for (const [key, adapter] of Object.entries(adapters)) {
      if (providerName.startsWith(key)) {
        return adapter;
      }
    }
    return null;
  }

  static supports(providerName = this.provider) {
    return !!this.getAdapter(providerName);
  }

  // ── TTS API ────────────────────────────────────────────────────

  static async init() {
    const adapter = this.getAdapter();
    if (adapter && typeof adapter.init === "function") {
      return await adapter.init();
    }
    console.warn(`[TTSManager] Provider ${this.provider} not initialized`);
  }

  static async queueText(
    text,
    playIcon,
    resolveCallback = null,
    provider = this.provider
  ) {
    const adapter = this.getAdapter(provider);
    if (adapter && typeof adapter.queueText === "function") {
      const voice = adapter.extractVoice
        ? adapter.extractVoice(provider)
        : null;
      return await adapter.queueText(text, playIcon, resolveCallback, voice);
    }
    console.warn(
      `[TTSManager] Provider ${provider} not supported for queueText`
    );
    if (resolveCallback) resolveCallback();
  }

  static async speakText(
    text,
    playIcon,
    resolveCallback = null,
    provider = this.provider
  ) {
    const adapter = this.getAdapter(provider);
    if (adapter && typeof adapter.speakText === "function") {
      const voice = adapter.extractVoice
        ? adapter.extractVoice(provider)
        : null;
      return await adapter.speakText(text, playIcon, resolveCallback, voice);
    }
    console.warn(
      `[TTSManager] Provider ${provider} not supported for speakText`
    );
    if (resolveCallback) resolveCallback();
  }

  static stopSpeech(playIcon = null, resolveCallback = null, provider = null) {
    const adapters = this.getActiveAdapters();

    if (provider) {
      const adapter = this.getAdapter(provider);
      if (adapter && typeof adapter.stopSpeech === "function") {
        adapter.stopSpeech(playIcon, resolveCallback);
      }
    } else {
      // Broadcast stop to all active adapters
      for (const adapter of Object.values(adapters)) {
        if (typeof adapter.stopSpeech === "function") {
          adapter.stopSpeech(playIcon, resolveCallback);
        }
      }
    }

    if (resolveCallback) resolveCallback();
  }
}

// Pre-warm the default provider on load (only on growtype chat page)
if (
  typeof document !== "undefined" &&
  document.body &&
  document.body.classList.contains("page-chat")
) {
  // TTSManager.init();
}
